import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Spinner, Form, Row, Col, Badge, Card } from "react-bootstrap";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";

import adminApi, { getAdminLookups } from "../../services/adminApi";
import ReusableAdminTable from "./ReusableAdminTable";

import "./AdminCostPage.css";

// ============================================
// HELPERS
// ============================================

const parseDetails = (details) => {
  try {
    if (typeof details === "object" && details !== null) return details;

    if (typeof details === "string") {
      return JSON.parse(details || "[]");
    }

    return [];
  } catch (e) {
    console.error("Error parsing details:", e);
    return [];
  }
};

const formatTypeName = (name) => {
  if (!name) return "";

  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase());
};

const AdminCostPage = () => {
  // ============================================
  // STATES
  // ============================================

  const [costs, setCosts] = useState([]);
  const [lookupGroups, setLookupGroups] = useState([]);

  const [view, setView] = useState("list");

  const [currentCost, setCurrentCost] = useState(null);

  const [loading, setLoading] = useState(false);

  const [originalDetails, setOriginalDetails] = useState([]);

  const [typeChangedDuringEdit, setTypeChangedDuringEdit] =
    useState(false);

  const [modalType, setModalType] = useState("new");

  // ============================================
  // FORM
  // ============================================

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    shouldUnregister: false,

    defaultValues: {
      title: "",
      type: "",
      valueType: "",
      details: [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "details",
  });

  const watchedType = watch("type");

  const watchedValueType = watch("valueType");

  // ============================================
  // FETCH COSTS
  // ============================================

  const fetchCosts = useCallback(async () => {
    setLoading(true);

    try {
      const res = await adminApi.get("/cost-list");

      setCosts(res?.data || []);
    } catch (e) {
      console.error("Error fetching costs:", e);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load costs",
        confirmButtonColor: "#c30000",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // FETCH LOOKUPS
  // ============================================

  const fetchLookups = useCallback(async () => {
    try {
      const res = await getAdminLookups();

      const filtered = res?.data?.filter((lk) =>
        ["auditionType", "jobRoleCategory", "projectType"].includes(
          lk.lookupGroup
        )
      );

      setLookupGroups(filtered || []);
    } catch (e) {
      console.error("Error fetching lookups:", e);
    }
  }, []);

  // ============================================
  // INITIAL LOAD
  // ============================================

  useEffect(() => {
    fetchCosts();
    fetchLookups();
  }, [fetchCosts, fetchLookups]);

  // ============================================
  // TYPE CHANGE
  // ============================================

  useEffect(() => {
    if (!watchedType) return;

    const selectedGroup = lookupGroups.find(
      (g) => g.lookupGroup === watchedType
    );

    if (selectedGroup?.lookupValue?.data) {
      const newDetails = selectedGroup.lookupValue.data.map((d) => ({
        value: "",
        label: d.lookupLabel,
      }));

      setValue("details", newDetails);

      if (view === "form" && currentCost && modalType === "edit") {
        setTypeChangedDuringEdit(true);
      }
    }
  }, [
    watchedType,
    lookupGroups,
    setValue,
    view,
    currentCost,
    modalType,
  ]);

  // ============================================
  // VALUE TYPE CHANGE
  // ============================================

  useEffect(() => {
    if (
      view === "form" &&
      modalType === "edit" &&
      !typeChangedDuringEdit
    ) {
      if (watchedValueType === currentCost?.valueType) {
        setValue("details", originalDetails);
      } else {
        const blankDetails = originalDetails.map((d) => ({
          label: d.label,
          value: "",
        }));

        setValue("details", blankDetails);
      }
    }
  }, [
    watchedValueType,
    setValue,
    originalDetails,
    currentCost,
    modalType,
    typeChangedDuringEdit,
    view,
  ]);

  // ============================================
  // RESET FORM
  // ============================================

  const resetForm = () => {
    reset({
      title: "",
      type: "",
      valueType: "",
      details: [],
    });

    setCurrentCost(null);

    setOriginalDetails([]);

    setTypeChangedDuringEdit(false);

    setShowForm(false);
  };

  // ============================================
  // SHOW FORM
  // ============================================

  const [showForm, setShowForm] = useState(false);

  const handleAdd = () => {
    setModalType("new");

    resetForm();

    setShowForm(true);

    setView("form");
  };

  // ============================================
  // EDIT
  // ============================================

  const handleEdit = (item) => {
    setModalType("edit");

    setCurrentCost(item);

    setTypeChangedDuringEdit(false);

    const parsedDetails = parseDetails(item.details);

    setOriginalDetails(parsedDetails);

    reset({
      title: item.title || "",
      type: item.type || "",
      valueType: item.valueType || "",
      details: parsedDetails,
    });

    setShowForm(true);

    setView("form");
  };

  // ============================================
  // DELETE
  // ============================================

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this cost item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c30000",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
      heightAuto: false,
      target: document.body,
    });

    if (!result.isConfirmed) return;

    try {
      await adminApi.delete(`/cost/${id}`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Cost Deleted Successfully",
        confirmButtonColor: "#2563eb",
      });

      fetchCosts();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          e.response?.data?.message ||
          e.message ||
          "Failed to delete cost",
        confirmButtonColor: "#c30000",
      });
    }
  };

  // ============================================
  // SUBMIT
  // ============================================

  const onSubmit = async (data) => {
    setLoading(true);

    const payload = {
      ...data,

      details: Array.isArray(data.details)
        ? data.details
        : [],
    };

    try {
      if (modalType === "edit") {
        await adminApi.put(
          `/cost/${currentCost.id}`,
          payload
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Cost Updated Successfully",
          confirmButtonColor: "#2563eb",
        });
      } else {
        await adminApi.post("/cost-save", payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Cost Saved Successfully",
          confirmButtonColor: "#2563eb",
        });
      }

      fetchCosts();

      resetForm();

      setView("list");
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          e.response?.data?.message ||
          e.message ||
          "Failed to save cost",
        confirmButtonColor: "#c30000",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // CANCEL
  // ============================================

  const handleCancel = async () => {
    const result = await Swal.fire({
      title: "Discard Changes?",
      text: "Unsaved changes will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c30000",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Cancel",
      heightAuto: false,
      target: document.body,
    });

    if (!result.isConfirmed) return;

    resetForm();

    setView("list");
  };

  // ============================================
  // TABLE COLUMNS
  // ============================================

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Title",
      },

      {
        accessorKey: "type",
        header: "Type",

        cell: (info) =>
          formatTypeName(info.getValue()),
      },

      {
        accessorKey: "valueType",
        header: "Value Type",

        cell: (info) => (
          <Badge bg="info">
            {info.getValue() === "percentage"
              ? "Percentage (%)"
              : info.getValue() === "fix"
              ? "Fixed"
              : "-"}
          </Badge>
        ),
      },

      {
        accessorKey: "details",
        header: "Items",

        cell: (info) => {
          const details = parseDetails(
            info.getValue()
          );

          return (
            <div className="cost-items">
              {details
                .filter(
                  (d) =>
                    d.value !== undefined &&
                    d.value !== ""
                )
                .map((d, i) => (
                  <div key={i}>
                    <strong>{d.label}</strong>:{" "}
                    {d.value}
                  </div>
                ))}
            </div>
          );
        },
      },

            {
        id: "actions",
        header: () => <div style={{ textAlign: "center", width: "100%" }}>ACTIONS</div>,
        cell: (info) => (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", width: "100%", margin: "0 auto" }}>
            {/* ✅ EDIT BUTTON: ROYAL BLUE */}
            <button
              type="button"
              style={{
                background: "#2563eb",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0
              }}
              onClick={() => handleEdit(info.row.original)}
              title="Edit Cost"
            >
              <FaEdit size={14} color="#ffffff" />
            </button>

            {/* ✅ DELETE BUTTON: CRIMSON RED */}
            <button
              type="button"
              style={{
                background: "#dc2626",
                backgroundColor: "#dc2626",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0
              }}
              onClick={() => handleDelete(info.row.original.id)}
              title="Delete Cost"
            >
              <FaTrash size={14} color="#ffffff" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="admin-cost-page">
      {view === "list" ? (
        <ReusableAdminTable
          data={costs}
          columns={columns}
          isLoading={loading}
          title="Cost Management"
          onAddNew={handleAdd}
          addNewButtonText="Add Cost"
          searchPlaceholder="Search costs..."
          exportCsvFilename="cost-management.csv"
        />
      ) : (
        <Card className="bg-dark border-secondary text-white">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3>
                {modalType === "edit"
                  ? "Edit Cost"
                  : "Add Cost"}
              </h3>

              <Button
                variant="secondary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>

            <Form onSubmit={handleSubmit(onSubmit)}>
              {/* TITLE */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Title / Group Name
                </Form.Label>

                <Controller
                  name="title"
                  control={control}
                  rules={{
                    required: "Title is required.",
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      placeholder="Enter title"
                      isInvalid={!!errors.title}
                      className="bg-dark text-white border-secondary"
                    />
                  )}
                />

                {errors.title && (
                  <Form.Control.Feedback type="invalid">
                    {errors.title.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              {/* TYPE */}
              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>

                <Controller
                  name="type"
                  control={control}
                  rules={{
                    required: "Type is required.",
                  }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      isInvalid={!!errors.type}
                      className="bg-dark text-white border-secondary"
                    >
                      <option value="">
                        -- Select Type --
                      </option>

                      {lookupGroups.map((lk) => (
                        <option
                          key={lk.lookupGroup}
                          value={lk.lookupGroup}
                        >
                          {formatTypeName(
                            lk.lookupGroup
                          )}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />

                {errors.type && (
                  <Form.Control.Feedback type="invalid">
                    {errors.type.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              {/* VALUE TYPE */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Value Type
                </Form.Label>

                <Controller
                  name="valueType"
                  control={control}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="bg-dark text-white border-secondary"
                    >
                      <option value="">
                        -- Select Value Type --
                      </option>

                      <option value="fix">
                        Fixed
                      </option>

                      <option value="percentage">
                        Percentage (%)
                      </option>
                    </Form.Select>
                  )}
                />
              </Form.Group>

              {/* DETAILS */}
              {fields.length > 0 && (
                <div className="mt-4 mb-3">
                  <h5 className="mb-3">
                    Items / Values
                  </h5>

                  {fields.map((item, i) => (
                    <Row
                      key={item.id}
                      className="mb-2 align-items-center"
                    >
                      <Col md={6}>
                        <Controller
                          name={`details.${i}.label`}
                          control={control}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              readOnly
                              className="bg-dark text-white border-secondary"
                            />
                          )}
                        />
                      </Col>

                      <Col md={6}>
                        <Controller
                          name={`details.${i}.value`}
                          control={control}
                          rules={{
                            validate: (
                              value
                            ) => {
                              if (
                                value === "" ||
                                value === null ||
                                value ===
                                  undefined
                              ) {
                                return true;
                              }

                              if (
                                isNaN(
                                  Number(value)
                                )
                              ) {
                                return "Value must be numeric.";
                              }

                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              placeholder="Enter value"
                              isInvalid={
                                !!errors
                                  .details?.[i]
                                  ?.value
                              }
                              className="bg-dark text-white border-secondary"
                            />
                          )}
                        />

                        {errors.details?.[i]
                          ?.value && (
                          <Form.Control.Feedback type="invalid">
                            {
                              errors.details[i]
                                .value.message
                            }
                          </Form.Control.Feedback>
                        )}
                      </Col>
                    </Row>
                  ))}
                </div>
              )}

              {/* BUTTON */}
              <Button
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                  />
                ) : modalType === "edit" ? (
                  "Update Cost"
                ) : (
                  "Save Cost"
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AdminCostPage;